/**
 * Utility functions for research templates functionality
 * These functions handle the API interactions for saving and applying research templates
 * Used by the Website Analyzer (Research Agent) tool
 */
import { authenticatedFetch } from './apiUtils';

/**
 * Get all research templates for the current user
 * @returns {Promise<Array>} - List of research templates
 */
export const getResearchTemplates = async () => {
  try {
    const response = await authenticatedFetch('/api/research/templates');

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch research templates');
    }

    const result = await response.json();
    return result.templates || [];
  } catch (error) {
    console.error('Get research templates error:', error);
    throw error;
  }
};

/**
 * Get a research template by ID
 * @param {string} templateId - ID of the template to retrieve
 * @returns {Promise<Object>} - Template details
 */
export const getResearchTemplateById = async (templateId) => {
  try {
    if (!templateId) {
      throw new Error('Template ID is required');
    }

    console.log(`[RESEARCH-TEMPLATE] Retrieving template with ID: ${templateId}`);
    const response = await authenticatedFetch(`/api/research/templates/${templateId}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch research template');
    }

    const result = await response.json();
    console.log('[RESEARCH-TEMPLATE] Retrieved template:', result);
    return result.template;
  } catch (error) {
    console.error(`Get research template by ID error for ${templateId}:`, error);
    throw error;
  }
};

/**
 * Save a new research template
 * @param {Object} templateData - Template data to save
 * @param {string} templateData.name - Name of the template
 * @param {string} templateData.description - Optional description
 * @param {string} templateData.instructions - Research instructions
 * @param {boolean} templateData.enableQualification - Enable qualification analysis
 * @param {boolean} templateData.enableCustomFields - Enable custom field extraction
 * @param {Array} templateData.customFields - Custom field definitions
 * @param {boolean} templateData.enableWebSearch - Enable web search
 * @param {string} templateData.webSearchInstructions - Web search instructions
 * @param {boolean} templateData.isFavorite - Whether template is marked as favorite
 * @returns {Promise<Object>} - Saved template data
 */
export const saveResearchTemplate = async (templateData) => {
  try {
    const {
      name,
      description,
      instructions,
      enableQualification,
      enableCustomFields,
      customFields,
      enableWebSearch,
      webSearchInstructions,
      isFavorite
    } = templateData;

    if (!name) {
      throw new Error('Template name is required');
    }

    if (!instructions) {
      throw new Error('Research instructions are required');
    }

    console.log('[RESEARCH-TEMPLATE] Saving template:', {
      name,
      hasInstructions: !!instructions,
      enableQualification,
      enableCustomFields,
      customFieldCount: customFields?.length || 0
    });

    const response = await authenticatedFetch('/api/research/templates', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name,
        description: description || '',
        instructions,
        enableQualification: enableQualification !== false,
        enableCustomFields: enableCustomFields || false,
        customFields: customFields || [],
        enableWebSearch: enableWebSearch || false,
        webSearchInstructions: webSearchInstructions || '',
        isFavorite: isFavorite || false
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to save research template');
    }

    const result = await response.json();
    console.log('[RESEARCH-TEMPLATE] Template saved successfully:', result);
    return result;
  } catch (error) {
    console.error('Save research template error:', error);
    throw error;
  }
};

/**
 * Apply a research template - returns configuration for form pre-filling
 * @param {string} templateId - ID of the template to apply
 * @returns {Promise<Object>} - Template configuration for pre-filling forms
 */
export const applyResearchTemplate = async (templateId) => {
  try {
    console.log('[RESEARCH-TEMPLATE] Applying template:', templateId);

    if (!templateId) {
      throw new Error('Template ID is required');
    }

    const response = await authenticatedFetch(`/api/research/templates/${templateId}/apply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to apply research template');
    }

    const result = await response.json();
    console.log('[RESEARCH-TEMPLATE] Template configuration retrieved:', result.config);

    return {
      success: true,
      config: result.config
    };
  } catch (error) {
    console.error('[RESEARCH-TEMPLATE] Error applying template:', error);
    throw error;
  }
};

/**
 * Update an existing research template
 * @param {string} templateId - ID of the template to update
 * @param {Object} templateData - Updated template data
 * @returns {Promise<Object>} - Updated template data
 */
export const updateResearchTemplate = async (templateId, templateData) => {
  try {
    if (!templateId) {
      throw new Error('Template ID is required');
    }

    console.log(`[RESEARCH-TEMPLATE] Updating template ${templateId}:`, templateData);

    const response = await authenticatedFetch(`/api/research/templates/${templateId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(templateData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update research template');
    }

    const result = await response.json();
    console.log('[RESEARCH-TEMPLATE] Template updated successfully:', result);
    return result;
  } catch (error) {
    console.error(`Update research template error for ${templateId}:`, error);
    throw error;
  }
};

/**
 * Delete a research template
 * @param {string} templateId - ID of the template to delete
 * @returns {Promise<Object>} - Result of deletion
 */
export const deleteResearchTemplate = async (templateId) => {
  try {
    if (!templateId) {
      throw new Error('Template ID is required');
    }

    const response = await authenticatedFetch(`/api/research/templates/${templateId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete research template');
    }

    return await response.json();
  } catch (error) {
    console.error('Delete research template error:', error);
    throw error;
  }
};

/**
 * Toggle a template's favorite status
 * @param {string} templateId - ID of the template to toggle favorite status
 * @returns {Promise<Object>} - Updated template data
 */
export const toggleResearchTemplateFavorite = async (templateId) => {
  try {
    if (!templateId) {
      throw new Error('Template ID is required');
    }

    console.log(`[RESEARCH-TEMPLATE] Toggling favorite status for template ${templateId}`);
    const response = await authenticatedFetch(`/api/research/templates/${templateId}/toggle-favorite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to toggle favorite status');
    }

    const result = await response.json();
    console.log('[RESEARCH-TEMPLATE] Toggle favorite response:', result);
    return result;
  } catch (error) {
    console.error(`Toggle favorite error for ${templateId}:`, error);
    throw error;
  }
};
